import { forwardRef, useCallback } from "react";

import { Box, Card, IconButton, Typography, makeStyles } from "@material-ui/core";
import { CustomContentProps, SnackbarContent, useSnackbar } from "notistack";
import {
  CheckCircleOutlinedIcon,
  CloseIcon,
  CrossCircleOutlinedIcon
} from "../../../icons";
import { Color } from "../../../theme";

interface SnackbarComponentProps extends CustomContentProps {
  title?: string;
}

const SnackbarComponent = forwardRef<HTMLDivElement, SnackbarComponentProps>(
  ({ id, ...props }, ref) => {
    const classes = useStyles({ variant: props.variant });
    const { closeSnackbar } = useSnackbar();

    const handleDismiss = useCallback(() => {
      closeSnackbar(id);
    }, [id, closeSnackbar]);

    return (
      <SnackbarContent ref={ref} className={classes.root}>
        <Card className={classes.card}>
          <Box className={classes.iconContainer}>
            {props.variant === "error" ? (
              <CrossCircleOutlinedIcon
                htmlColor={Color.White}
                style={{ width: "1rem", height: "1rem" }}
              />
            ) : (
              <CheckCircleOutlinedIcon
                htmlColor={Color.White}
                style={{ width: "1rem", height: "1rem" }}
              />
            )}
          </Box>
          <Box className={classes.contentWithButtonContainer}>
            <Box className={classes.contentContainer}>
              {props.title && <Typography variant="h6">{props.title}</Typography>}

              {props.message && (
                <Typography variant="body1" style={{ lineHeight: "1.5rem" }}>
                  {props.message}
                </Typography>
              )}
            </Box>
            <IconButton className={classes.closeButton} onClick={handleDismiss}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Card>
      </SnackbarContent>
    );
  }
);

const useStyles = makeStyles((theme) => ({
  root: {
    "@media (min-width:600px)": {
      minWidth: "344px !important"
    }
  },
  card: (props: { variant: SnackbarComponentProps["variant"] }) => ({
    width: "100%",
    borderRadius: "0.5rem",
    borderLeft: `6px solid ${
      props.variant === "success"
        ? Color.CheckGreen
        : props.variant === "error"
        ? Color.ErrorLight
        : Color.Information
    }`,
    backgroundColor:
      props.variant === "success"
        ? Color.GreenLight
        : props.variant === "error"
        ? "#F8DADA"
        : Color.White,
    display: "flex",
    padding: "1rem 1rem 1rem 1.5rem",
    alignItems: "flex-start",
    gap: "1.5625rem"
  }),
  iconContainer: (props: { variant: SnackbarComponentProps["variant"] }) => ({
    width: "2.125rem",
    height: "2.125rem",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
    borderRadius: "0.375rem",
    backgroundColor:
      props.variant === "success"
        ? Color.CheckGreen
        : props.variant === "error"
        ? Color.ErrorLight
        : Color.Information
  }),
  contentContainer: {
    display: "block"
  },
  contentWithButtonContainer: {
    display: "flex",
    justifyContent: "space-between",
    gap: "0.5rem",
    width: "100%"
  },
  closeButton: {
    padding: "4px",
    marginLeft: "auto",
    marginBottom: "auto",
    marginTop: "-8px"
  }
}));

SnackbarComponent.displayName = "SnackbarComponent";

export default SnackbarComponent;
